import { formatPageUnits } from "sketch/units";
import { SnapIndicatorOverlayUtil, type TLSnapIndicatorOverlay } from "tldraw";

/**
 * tldraw's snap guides, with measurements: the default alignment lines and
 * equal-gap brackets render as usual (super.render), then every gap and
 * every adjacent pair of aligned points gets its distance written on it.
 * Registered with the same static type, so it replaces the default overlay.
 */
const MIN_LABEL_PX = 28;

export class MeasuredSnapOverlayUtil extends SnapIndicatorOverlayUtil {
  override render(
    ctx: CanvasRenderingContext2D,
    overlays: TLSnapIndicatorOverlay[],
  ): void {
    super.render(ctx, overlays);
    const zoom = this.editor.getZoomLevel();
    const themeColors =
      this.editor.getCurrentTheme().colors[this.editor.getColorMode()];
    ctx.font = `${14 / zoom}px tldraw_draw, sans-serif`;
    ctx.fillStyle = themeColors.snap;
    // A background halo keeps the measurement legible over content.
    ctx.strokeStyle = themeColors.background ?? "#ffffff";
    ctx.lineWidth = 3 / zoom;
    ctx.lineJoin = "round";

    const label = (text: string, x: number, y: number, horizontal: boolean) => {
      if (horizontal) {
        ctx.textAlign = "center";
        ctx.textBaseline = "bottom";
        ctx.strokeText(text, x, y - 5 / zoom);
        ctx.fillText(text, x, y - 5 / zoom);
      } else {
        ctx.textAlign = "left";
        ctx.textBaseline = "middle";
        ctx.strokeText(text, x + 7 / zoom, y);
        ctx.fillText(text, x + 7 / zoom, y);
      }
    };

    for (const overlay of overlays) {
      const { line } = overlay.props;
      if (line.type === "gaps") {
        const horizontal = line.direction === "horizontal";
        for (const gap of line.gaps) {
          const s = {
            x: (gap.startEdge[0].x + gap.startEdge[1].x) / 2,
            y: (gap.startEdge[0].y + gap.startEdge[1].y) / 2,
          };
          const e = {
            x: (gap.endEdge[0].x + gap.endEdge[1].x) / 2,
            y: (gap.endEdge[0].y + gap.endEdge[1].y) / 2,
          };
          const d = horizontal ? Math.abs(e.x - s.x) : Math.abs(e.y - s.y);
          if (d * zoom < MIN_LABEL_PX) continue;
          label(
            formatPageUnits(d) || `0"`,
            (s.x + e.x) / 2,
            (s.y + e.y) / 2,
            horizontal,
          );
        }
      } else if (line.type === "points" && line.points.length > 1) {
        // An alignment line: measure between adjacent aligned points.
        const xs = line.points.map((p) => p.x);
        const horizontal =
          Math.max(...xs) - Math.min(...xs) >
          Math.max(...line.points.map((p) => p.y)) -
            Math.min(...line.points.map((p) => p.y));
        const sorted = [...line.points].sort((a, b) =>
          horizontal ? a.x - b.x : a.y - b.y,
        );
        for (let i = 1; i < sorted.length; i++) {
          const a = sorted[i - 1]!;
          const b = sorted[i]!;
          const d = horizontal ? b.x - a.x : b.y - a.y;
          if (d * zoom < MIN_LABEL_PX) continue;
          label(
            formatPageUnits(d) || `0"`,
            (a.x + b.x) / 2,
            (a.y + b.y) / 2,
            horizontal,
          );
        }
      }
    }
  }
}
